import { canonicalJson } from './pack-engine';

/**
 * These blocks make the mandatory notice content reviewable data rather than
 * editable rendering logic. The primary source was read on 2026-09-22.
 */
export const COMPLAINT_OUTCOME_LETTER_REQUIRED_ELEMENTS = [
  {
    key: 'outcome',
    citation: 'Aged Care Rules 2025 r 165-25',
    unverified: false,
  },
  {
    key: 'reasons',
    citation: 'Aged Care Rules 2025 r 165-25',
    unverified: false,
  },
  {
    key: 'complaints_commissioner',
    citation: 'Aged Care Rules 2025 r 165-25',
    unverified: false,
  },
  {
    key: 'advocacy_opan',
    citation: 'Aged Care Rules 2025 r 165-25',
    unverified: false,
  },
] as const;

export type ComplaintOutcomeLetterElement =
  (typeof COMPLAINT_OUTCOME_LETTER_REQUIRED_ELEMENTS)[number]['key'];

export interface ComplaintOutcomeLetterTemplate {
  version: number;
  wording: Record<ComplaintOutcomeLetterElement, string>;
}

export interface BuildComplaintOutcomeLetterPackInput {
  complaintId: string;
  isAnonymous: boolean;
  complainantName: string;
  outcome: string;
  reasons: string;
  template: ComplaintOutcomeLetterTemplate;
  producedAt: string;
  sha256Hex: (canonical: string) => string;
}

export type ComplaintOutcomeLetterPack = {
  kind: 'COMPLAINT_OUTCOME_LETTER';
  manifest: {
    complaintId: string;
    templateVersion: number;
    legalCitation: 'Aged Care Rules 2025 r 165-25';
    minimumNecessary: readonly ['complainant_name', 'outcome', 'reasons'];
  };
  sections: readonly { key: ComplaintOutcomeLetterElement; text: string }[];
  contentDigest: string;
};

export type ComplaintOutcomeLetterSkip = {
  kind: 'COMPLAINT_OUTCOME_LETTER_SKIP';
  skipped: 'ANONYMOUS';
  reason: 'Anonymous complaint: no outcome letter is produced.';
};

function requiredText(value: string, code: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(code);
  return trimmed;
}

/**
 * Produces deterministic, minimum-necessary letter content. A caller supplies
 * the clock and digest implementation so this package remains pure.
 */
export function buildComplaintOutcomeLetterPack(
  input: BuildComplaintOutcomeLetterPackInput,
): ComplaintOutcomeLetterPack | ComplaintOutcomeLetterSkip {
  if (input.isAnonymous) {
    return {
      kind: 'COMPLAINT_OUTCOME_LETTER_SKIP',
      skipped: 'ANONYMOUS',
      reason: 'Anonymous complaint: no outcome letter is produced.',
    };
  }
  const complaintId = requiredText(
    input.complaintId,
    'complaint_outcome_letter_complaint_required',
  );
  const complainantName = requiredText(
    input.complainantName,
    'complaint_outcome_letter_name_required',
  );
  const outcome = requiredText(input.outcome, 'complaint_outcome_letter_outcome_required');
  const reasons = requiredText(input.reasons, 'complaint_outcome_letter_reasons_required');
  if (!Number.isInteger(input.template.version) || input.template.version < 1) {
    throw new Error('complaint_outcome_letter_template_version_invalid');
  }
  const sections = COMPLAINT_OUTCOME_LETTER_REQUIRED_ELEMENTS.map((element) => {
    const wording = requiredText(
      input.template.wording[element.key],
      `complaint_outcome_letter_template_${element.key}_required`,
    );
    const value = element.key === 'outcome' ? outcome : element.key === 'reasons' ? reasons : '';
    return {
      key: element.key,
      text: wording
        .replaceAll('{{complainant_name}}', complainantName)
        .replaceAll('{{value}}', value),
    };
  });
  const draft: Omit<ComplaintOutcomeLetterPack, 'contentDigest'> = {
    kind: 'COMPLAINT_OUTCOME_LETTER',
    manifest: {
      complaintId,
      templateVersion: input.template.version,
      legalCitation: 'Aged Care Rules 2025 r 165-25',
      minimumNecessary: ['complainant_name', 'outcome', 'reasons'],
    },
    sections,
  };
  return {
    ...draft,
    contentDigest: input.sha256Hex(canonicalJson(draft)),
  };
}
